import { VideoFormatEnum } from '@/app/common/general-types/briefs.dto';
import { VideoCreatorResponseDto, VideoResponseDto, VideoResponseWithBriefDto } from '@/app/common/general-types/videos.dto';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import Hls from 'hls.js';

@Component({
  selector: 'mgc-portfolio-inline-video',
  imports: [VideoThumbnailComponent],
  templateUrl: './mgc-portfolio-inline-video.component.html',
  styleUrl: './mgc-portfolio-inline-video.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MgcPortfolioInlineVideoComponent {
  readonly video = input.required<VideoResponseWithBriefDto | VideoCreatorResponseDto | VideoResponseDto>();
  readonly profileFrame = input(false);

  private readonly destroyRef = inject(DestroyRef);

  readonly playbackActive = signal(false);
  readonly isPlaying = signal(false);
  readonly currentTimeSec = signal(0);
  readonly durationSec = signal(0);
  readonly isMuted = signal(false);
  readonly isScrubbing = signal(false);

  readonly progressPercent = computed(() => {
    const d = this.durationSec();
    if (!d || d <= 0 || !Number.isFinite(d)) {
      return 0;
    }
    return Math.min(100, (this.currentTimeSec() / d) * 100);
  });

  readonly ariaTimeNow = computed(() => Math.round(this.currentTimeSec()));

  readonly ariaDurationMax = computed(() => {
    const d = this.durationSec();
    return Number.isFinite(d) && d > 0 ? Math.round(d) : 1;
  });

  private readonly videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  private hlsInstance: Hls | null = null;
  private sourcesAttached = false;
  private previousVideoId: string | null = null;

  readonly videoId = computed(() => this.video().id);

  readonly isHorizontal = computed(
    () => this.video().videoFormat === VideoFormatEnum.Horizontal,
  );

  readonly videoFormatForAtom = computed(
    () => (this.video().videoFormat ?? VideoFormatEnum.Vertical) as VideoFormatEnum,
  );

  readonly thumbnailUrl = computed(() => {
    const u = this.video().thumbnailURL;
    return u ? u : undefined;
  });

  readonly thumbnailCdnKey = computed(() => {
    const k = this.video().thumbnailCdnKey;
    return k ? k : undefined;
  });

  constructor() {
    effect(() => {
      this.videoId();
      untracked(() => {
        const id = this.video().id;
        if (this.previousVideoId !== null && this.previousVideoId !== id) {
          this.teardownPlayback();
          this.playbackActive.set(false);
          this.sourcesAttached = false;
          this.isPlaying.set(false);
          this.currentTimeSec.set(0);
          this.durationSec.set(0);
          this.isMuted.set(false);
          this.isScrubbing.set(false);
        }
        this.previousVideoId = id;
      });
    });

    effect(() => {
      const active = this.playbackActive();
      const ref = this.videoPlayer();
      if (active && ref) {
        untracked(() => {
          this.attachSourcesOnce(ref.nativeElement);
        });
      }
    });

    this.destroyRef.onDestroy(() => {
      this.teardownPlayback();
    });
  }

  onPlayClick(): void {
    this.playbackActive.set(true);
  }

  onControlBarPlayPause(event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.videoPlayer()?.nativeElement;
    if (!ref) {
      return;
    }
    if (ref.paused) {
      void ref.play();
    } else {
      ref.pause();
    }
  }

  onVideoPlay(): void {
    this.isPlaying.set(true);
  }

  onVideoPause(): void {
    this.isPlaying.set(false);
  }

  onTimeUpdate(): void {
    if (this.isScrubbing()) {
      return;
    }
    const el = this.videoPlayer()?.nativeElement;
    if (!el) {
      return;
    }
    this.currentTimeSec.set(el.currentTime);
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      this.durationSec.set(d);
    }
  }

  onLoadedMetadata(): void {
    const el = this.videoPlayer()?.nativeElement;
    if (!el) {
      return;
    }
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      this.durationSec.set(d);
    }
    this.isMuted.set(el.muted);
  }

  onVolumeChange(): void {
    const el = this.videoPlayer()?.nativeElement;
    if (el) {
      this.isMuted.set(el.muted);
    }
  }

  onToggleMute(event: MouseEvent): void {
    event.stopPropagation();
    const el = this.videoPlayer()?.nativeElement;
    if (!el) {
      return;
    }
    el.muted = !el.muted;
    this.isMuted.set(el.muted);
  }

  onProgressKeyDown(event: KeyboardEvent): void {
    const el = this.videoPlayer()?.nativeElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) {
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -5 : 5;
      const next = Math.min(el.duration, Math.max(0, el.currentTime + delta));
      el.currentTime = next;
      this.currentTimeSec.set(next);
    }
  }

  onProgressPointerDown(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const el = this.videoPlayer()?.nativeElement;
    const bar = event.currentTarget as HTMLElement;
    if (!el || !bar) {
      return;
    }
    this.isScrubbing.set(true);
    this.seekFromClientX(event, el, bar);
    const move = (e: MouseEvent | TouchEvent): void => {
      e.preventDefault();
      const v = this.videoPlayer()?.nativeElement;
      if (v) {
        this.seekFromClientX(e, v, bar);
      }
    };
    const stop = (): void => {
      this.isScrubbing.set(false);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', stop);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', stop);
  }

  formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  }

  private seekFromClientX(
    event: MouseEvent | TouchEvent,
    video: HTMLVideoElement,
    bar: HTMLElement,
  ): void {
    const rect = bar.getBoundingClientRect();
    const clientX = this.readClientX(event);
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const dur = video.duration;
    if (!Number.isFinite(dur) || dur <= 0) {
      return;
    }
    const t = pct * dur;
    video.currentTime = t;
    this.currentTimeSec.set(t);
  }

  private readClientX(event: MouseEvent | TouchEvent): number {
    if ('touches' in event && event.touches.length > 0) {
      return event.touches[0].clientX;
    }
    if ('changedTouches' in event && event.changedTouches.length > 0) {
      return event.changedTouches[0].clientX;
    }
    return (event as MouseEvent).clientX;
  }

  private attachSourcesOnce(el: HTMLVideoElement): void {
    if (this.sourcesAttached) {
      return;
    }
    this.sourcesAttached = true;
    const v = this.video();
    const hlsUrl = v.hlsUrl?.trim();

    if (hlsUrl) {
      if (Hls.isSupported()) {
        const hls = new Hls();
        this.hlsInstance = hls;
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            this.fallbackToProgressive(el, v.fullUrl);
          }
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(el);
      } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = hlsUrl;
        el.addEventListener(
          'error',
          () => {
            this.fallbackToProgressive(el, v.fullUrl);
          },
          { once: true },
        );
      } else {
        el.src = v.fullUrl;
      }
    } else {
      el.src = v.fullUrl;
    }

    void el.play().catch(() => {
      /* autoplay policy or decode errors */
    });
  }

  private fallbackToProgressive(el: HTMLVideoElement, fullUrl: string): void {
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
    el.removeAttribute('src');
    el.load();
    el.src = fullUrl;
    void el.play().catch(() => {
      /* ignore */
    });
  }

  private teardownPlayback(): void {
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
    const ref = this.videoPlayer()?.nativeElement;
    if (ref) {
      ref.pause();
      ref.removeAttribute('src');
      ref.load();
    }
    this.currentTimeSec.set(0);
    this.durationSec.set(0);
    this.isMuted.set(false);
    this.isScrubbing.set(false);
  }
}
